import {
    buildChatTimeOptions,
    chatTimeToMinutes,
    getChatEndTimeOptions,
    getChatStartTimeOptions,
    normalizeChatDayTimes,
    type ChatScheduleDay,
} from "@/domains/shared/models/chatSchedule";

import type { IUser } from "@/domains/shared/models/IUser";
import { getTranslation } from "@mivo/i18n";
import { WORK_SCHEDULE_DAY_TRANSLATIONS as T } from "./clinicUiConstants";

export type WorkScheduleStatus = "none" | "proposed" | "approved";

export type WorkScheduleSlot = {
    dayOfWeek: number;
    startMinutes: number;
    endMinutes: number;
};

export type WorkScheduleResponse = {
    status: WorkScheduleStatus;
    proposedByUserId?: string | null;
    approvedAt?: string | null;
    slots: WorkScheduleSlot[];
};


export type WorkScheduleDay = ChatScheduleDay;

export function workScheduleDayLabel(dayOfWeek: number): string {
    switch (dayOfWeek) {
        case 0:
            return getTranslation(T.sunday);
        case 1:
            return getTranslation(T.monday);
        case 2:
            return getTranslation(T.tuesday);
        case 3:
            return getTranslation(T.wednesday);
        case 4:
            return getTranslation(T.thursday);
        case 5:
            return getTranslation(T.friday);
        case 6:
            return getTranslation(T.saturday);
        default:
            return String(dayOfWeek);
    }
}

/** Locale-aware day labels; prefer workScheduleDayLabel(day) at call sites. */
export const WORK_SCHEDULE_DAY_LABELS: Record<number, string> = {
    get 0() {
        return workScheduleDayLabel(0);
    },
    get 1() {
        return workScheduleDayLabel(1);
    },
    get 2() {
        return workScheduleDayLabel(2);
    },
    get 3() {
        return workScheduleDayLabel(3);
    },
    get 4() {
        return workScheduleDayLabel(4);
    },
    get 5() {
        return workScheduleDayLabel(5);
    },
    get 6() {
        return workScheduleDayLabel(6);
    },
};

export const WORK_SCHEDULE_WEEKDAY_INDICES = [1, 2, 3, 4, 5];
export const WORK_SCHEDULE_WEEKEND_INDICES = [0, 6];

export const WORK_SCHEDULE_DEFAULT_START = "09:00";
export const WORK_SCHEDULE_DEFAULT_END = "17:00";

export const WORK_TIME_OPTIONS = buildChatTimeOptions();

export function defaultWorkScheduleDays(): WorkScheduleDay[] {
    return Array.from({ length: 7 }, (_, dayOfWeek) => ({
        dayOfWeek,
        enabled: false,
        startTime: WORK_SCHEDULE_DEFAULT_START,
        endTime: WORK_SCHEDULE_DEFAULT_END,
    }));
}

export function minutesToWorkTime(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function workTimeToMinutes(time: string): number {
    return chatTimeToMinutes(time);
}

export function workScheduleDaysToSlots(days: WorkScheduleDay[]): WorkScheduleSlot[] {
    return days
        .filter((d) => d.enabled)
        .map((d) => ({
            dayOfWeek: d.dayOfWeek,
            startMinutes: workTimeToMinutes(d.startTime),
            endMinutes: workTimeToMinutes(d.endTime),
        }));
}

export function slotsToWorkScheduleDays(slots: WorkScheduleSlot[]): WorkScheduleDay[] {
    const byDay = new Map(slots.map((s) => [s.dayOfWeek, s]));
    return defaultWorkScheduleDays().map((day) => {
        const slot = byDay.get(day.dayOfWeek);
        if (!slot) {
            return day;
        }
        return normalizeWorkDayTimes({
            dayOfWeek: day.dayOfWeek,
            enabled: true,
            startTime: minutesToWorkTime(slot.startMinutes),
            endTime: minutesToWorkTime(slot.endMinutes),
        });
    });
}

export function normalizeWorkDayTimes(day: WorkScheduleDay): WorkScheduleDay {
    return normalizeChatDayTimes(day, WORK_TIME_OPTIONS);
}

export function hasAnyWorkDayEnabled(days: WorkScheduleDay[]): boolean {
    return days.some((d) => d.enabled);
}

export function formatWorkScheduleSlot(slot: WorkScheduleSlot): string {
    return `${WORK_SCHEDULE_DAY_LABELS[slot.dayOfWeek]} · ${minutesToWorkTime(slot.startMinutes)}–${minutesToWorkTime(slot.endMinutes)}`;
}

export function formatWorkScheduleSlots(slots: WorkScheduleSlot[]): string[] {
    return [...slots]
        .sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.startMinutes - b.startMinutes)
        .map(formatWorkScheduleSlot);
}

export { getChatStartTimeOptions as getWorkStartTimeOptions, getChatEndTimeOptions as getWorkEndTimeOptions };

export const AGENDA_DEFAULT_START_MINUTES = 6 * 60;
export const AGENDA_DEFAULT_END_MINUTES = 23 * 60;
export const AGENDA_SLOT_STEP_MINUTES = 5;

export function getApprovedWorkScheduleSlots(response: WorkScheduleResponse | undefined): WorkScheduleSlot[] {
    return response?.status === "approved" && response.slots.length > 0 ? response.slots : [];
}

export function resolveTherapistMembershipId(
    user: Pick<IUser, "id" | "activeClinicId" | "activeClinicMembershipId" | "clinicProfessionals"> | null | undefined,
    therapistUserId: string | null | undefined,
): string | null {
    if (!user?.activeClinicId || !therapistUserId) {
        return null;
    }
    if (therapistUserId === user.id) {
        return user.activeClinicMembershipId ?? null;
    }
    return user.clinicProfessionals?.find((p) => p.id === therapistUserId)?.membershipId ?? null;
}

export function getWorkScheduleSlotForDay(slots: WorkScheduleSlot[], dayOfWeek: number): WorkScheduleSlot | null {
    return slots.find((s) => s.dayOfWeek === dayOfWeek) ?? null;
}

export function getDayOfWeekInTimeZone(date: Date, timeZoneId: string): number {
    const weekday = new Intl.DateTimeFormat("en-US", { timeZone: timeZoneId, weekday: "short" }).format(date);
    const map: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    return map[weekday] ?? date.getDay();
}

export function isStartWithinApprovedWorkSchedule(
    slots: WorkScheduleSlot[],
    dayOfWeek: number,
    startMinutes: number,
): boolean {
    if (!slots.length) {
        return true;
    }
    const day = getWorkScheduleSlotForDay(slots, dayOfWeek);
    if (!day) {
        return false;
    }
    return startMinutes >= day.startMinutes && startMinutes < day.endMinutes;
}

export function resolveSessionTimeBoundsForDay(
    dayOfWeek: number,
    approvedWorkSlots: WorkScheduleSlot[] | undefined,
    globalMin = AGENDA_DEFAULT_START_MINUTES,
    globalMax = AGENDA_DEFAULT_END_MINUTES,
): { minMinutes: number; maxMinutes: number; dayEnabled: boolean } {
    if (!approvedWorkSlots?.length) {
        return { minMinutes: globalMin, maxMinutes: globalMax, dayEnabled: true };
    }
    const day = getWorkScheduleSlotForDay(approvedWorkSlots, dayOfWeek);
    if (!day) {
        return { minMinutes: globalMin, maxMinutes: globalMin, dayEnabled: false };
    }
    return {
        minMinutes: Math.max(globalMin, day.startMinutes),
        maxMinutes: Math.min(globalMax, day.endMinutes),
        dayEnabled: true,
    };
}

export function agendaEnvelopeFromWorkSchedule(
    slots: WorkScheduleSlot[],
): { minMinutes: number; maxMinutes: number } | null {
    if (!slots.length) {
        return null;
    }
    return {
        minMinutes: Math.min(...slots.map((s) => s.startMinutes)),
        maxMinutes: Math.max(...slots.map((s) => s.endMinutes)),
    };
}

export function resolveAgendaCalendarBounds(
    slots: WorkScheduleSlot[],
    view: "month" | "week" | "day",
    focusedDate: Date,
    timeZoneId: string,
    globalMin = AGENDA_DEFAULT_START_MINUTES,
    globalMax = AGENDA_DEFAULT_END_MINUTES,
): { minMinutes: number; maxMinutes: number } {
    if (!slots.length) {
        return { minMinutes: globalMin, maxMinutes: globalMax };
    }
    if (view === "day") {
        const dayOfWeek = getDayOfWeekInTimeZone(focusedDate, timeZoneId);
        const bounds = resolveSessionTimeBoundsForDay(dayOfWeek, slots, globalMin, globalMax);
        if (!bounds.dayEnabled) {
            return { minMinutes: globalMin, maxMinutes: Math.min(globalMax, globalMin + 60) };
        }
        return { minMinutes: bounds.minMinutes, maxMinutes: bounds.maxMinutes };
    }
    const envelope = agendaEnvelopeFromWorkSchedule(slots);
    if (!envelope) {
        return { minMinutes: globalMin, maxMinutes: globalMax };
    }
    return {
        minMinutes: Math.max(globalMin, envelope.minMinutes),
        maxMinutes: Math.min(globalMax, envelope.maxMinutes),
    };
}

export function minutesToAgendaWallDate(minutes: number): Date {
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    return new Date(1970, 0, 1, hour, minute, 0);
}
